<template>
  <v-container>
    <v-tabs
      v-model="tab"
      class="mb-3"
    >
      <v-tab
        v-for="s in steps"
        :key="s"
      >
        ຂັ້ນທີ {{ s }}{{ s === 3 ? ` (${finalWord}ຈິງ)` : '' }}
        <v-chip
          x-small
          class="ml-2"
          :color="counts[s] ? 'amber lighten-4' : 'grey lighten-3'"
        >
          {{ counts[s] || 0 }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <div
      v-if="!steps.length"
      class="pa-6 text-center grey--text"
    >
      ທ່ານບໍ່ມີສິດອະນຸມັດຂັ້ນໃດ
    </div>

    <template v-else>
      <v-card
        outlined
        class="pa-3 mb-3"
      >
        <v-row dense>
          <v-col
            cols="12"
            md="5"
          >
            <v-text-field
              v-model="filter"
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              :label="searchLabel"
              @keyup.enter="load"
              @click:clear="clearFilter"
            />
          </v-col>
          <v-col
            cols="12"
            md="7"
            class="d-flex align-center flex-wrap"
          >
            <v-btn
              color="primary"
              depressed
              class="mr-2 mb-1"
              @click="load"
            >
              <v-icon left>
                mdi-magnify
              </v-icon>
              ຄົ້ນຫາ
            </v-btn>
            <v-spacer />
            <v-btn
              text
              color="red"
              class="mr-2 mb-1"
              :disabled="!selected.length"
              @click="openReject"
            >
              ປະຕິເສດ{{ selected.length ? ` (${selected.length} ລາຍ)` : '' }}
            </v-btn>
            <v-btn
              :color="step === 3 ? 'error' : 'primary'"
              depressed
              class="mb-1"
              :disabled="!selected.length"
              @click="approveDialog = true"
            >
              <v-icon left>
                {{ step === 3 ? finalIcon : 'mdi-check' }}
              </v-icon>
              {{ step === 3 ? `ອະນຸມັດ ແລະ ${finalWord}` : `ອະນຸມັດຂັ້ນທີ ${step}` }}{{ selected.length ? ` (${selected.length} ລາຍ)` : '' }}
            </v-btn>
          </v-col>
        </v-row>
        <div class="text-caption grey--text mt-2">
          ເລືອກໄດ້ຫຼາຍລາຍການພ້ອມກັນ. ຄຳຂໍທີ່ທ່ານເປັນຜູ້ສົ່ງ ຫຼື ໄດ້ອະນຸມັດຂັ້ນກ່ອນແລ້ວ ເລືອກບໍ່ໄດ້ (ແຕ່ລະຂັ້ນຕ້ອງຄົນລະຄົນ).
        </div>
      </v-card>

      <v-alert
        v-if="failed.length"
        type="warning"
        text
        dense
        dismissible
        @input="failed = []"
      >
        ບໍ່ສຳເລັດ {{ failed.length }} ລາຍການ:
        <div
          v-for="f in failed"
          :key="f.id"
          class="text-caption"
        >
          #{{ f.id }}: {{ f.message }}
        </div>
      </v-alert>

      <v-card outlined>
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="requests"
          :loading="loading"
          item-key="id"
          show-select
          selectable-key="selectable"
          :item-class="(item) => (item.selectable ? '' : 'grey lighten-4')"
          :items-per-page="50"
          no-data-text="ບໍ່ມີຄຳຂໍທີ່ລໍຖ້າຂັ້ນນີ້"
        >
          <template v-slot:[`item.customer_name`]="{ item }">
            <div>{{ item.customer_name }}</div>
            <div class="text-caption grey--text">
              {{ item.customer_code }} · {{ item.customer_phone }}
            </div>
          </template>
          <template v-slot:[`item.bill_number`]="{ item }">
            <div>{{ item.bill_number }}</div>
            <div class="text-caption grey--text">
              {{ item.content }} · {{ billMonth(item) }}
            </div>
          </template>
          <template v-slot:[`item.total`]="{ item }">
            {{ money(item.total) }}
          </template>
          <template v-slot:[`item.to_status`]="{ item }">
            <span :class="item.to_status === 'active' ? 'green--text' : 'red--text'">
              {{ statusChange(item) }}
            </span>
          </template>
          <template v-slot:[`item.reason`]="{ item }">
            <div style="min-width: 160px">
              {{ item.reason }}
            </div>
            <v-chip
              v-if="item.source === 'import'"
              x-small
              outlined
            >
              Excel
            </v-chip>
          </template>
          <template v-slot:[`item.trail`]="{ item }">
            <div class="text-caption">
              ສົ່ງ: {{ item.submitter && item.submitter.name }} · {{ item.created_at }}
            </div>
            <div
              v-if="item.approver1"
              class="text-caption"
            >
              ຂັ້ນ 1: {{ item.approver1.name }}
            </div>
            <div
              v-if="item.approver2"
              class="text-caption"
            >
              ຂັ້ນ 2: {{ item.approver2.name }}
            </div>
            <div
              v-if="item.acted_by_me"
              class="text-caption orange--text text--darken-2"
            >
              ທ່ານໄດ້ສົ່ງ/ອະນຸມັດແລ້ວ
            </div>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <v-dialog
      v-model="approveDialog"
      max-width="460"
    >
      <v-card>
        <v-card-title>{{ step === 3 ? `ຢືນຢັນ${finalWord}` : `ຢືນຢັນອະນຸມັດຂັ້ນທີ ${step}` }}</v-card-title>
        <v-card-text>
          <template v-if="step === 3">
            {{ finalConfirm(selected.length) }}
          </template>
          <template v-else>
            ອະນຸມັດ {{ selected.length }} ລາຍ ແລ້ວສົ່ງຕໍ່ໃຫ້ຂັ້ນທີ {{ step + 1 }}?
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="approveDialog = false"
          >
            ຍົກເລີກ
          </v-btn>
          <v-btn
            :color="step === 3 ? 'error' : 'primary'"
            depressed
            :loading="working"
            @click="approve"
          >
            {{ step === 3 ? `ອະນຸມັດ ແລະ ${finalWord}` : 'ອະນຸມັດ' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="rejectDialog"
      max-width="460"
    >
      <v-card>
        <v-card-title>ປະຕິເສດ {{ selected.length }} ລາຍ</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="rejectReason"
            outlined
            rows="2"
            auto-grow
            label="ເຫດຜົນທີ່ປະຕິເສດ *"
            :error-messages="rejectError"
            @input="rejectError = ''"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="rejectDialog = false"
          >
            ຍົກເລີກ
          </v-btn>
          <v-btn
            color="red"
            dark
            depressed
            :loading="working"
            @click="reject"
          >
            ປະຕິເສດ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { money, statusChange, billMonth } from './cells';

/**
 * The approval page of a household three-step request: one tab per step the user may approve,
 * select several and approve or reject at once. Columns with value customer_name, bill_number,
 * total, to_status, reason or trail get their special cell.
 */
export default {
  name: 'ThreeStepApproval',
  props: {
    api: { type: String, required: true },
    permPrefix: { type: String, required: true },
    columns: { type: Array, required: true },
    finalWord: { type: String, default: 'ລຶບ' },
    finalIcon: { type: String, default: 'mdi-delete' },
    finalConfirm: { type: Function, default: (n) => `ອະນຸມັດ ${n} ລາຍ?` },
    searchLabel: { type: String, default: 'ຄົ້ນຫາ: ຊື່, ເບີໂທ, CUS_' },
  },
  data() {
    return {
      tab: 0,
      counts: {},
      filter: '',
      requests: [],
      selected: [],
      failed: [],
      loading: false,
      working: false,
      approveDialog: false,
      rejectDialog: false,
      rejectReason: '',
      rejectError: '',
    };
  },
  computed: {
    steps() {
      return [1, 2, 3].filter((s) => this.$can(`${this.permPrefix}_${s}`));
    },
    step() {
      return this.steps[this.tab];
    },
    headers() {
      return [{ text: 'ເລກຄຳຂໍ', value: 'id' }, ...this.columns, { text: 'ເຫດຜົນ', value: 'reason', sortable: false },
        { text: 'ຜູ້ສົ່ງ / ອະນຸມັດແລ້ວ', value: 'trail', sortable: false }];
    },
  },
  watch: {
    tab() {
      this.selected = [];
      this.failed = [];
      this.load();
    },
  },
  created() {
    if (this.steps.length) {
      this.loadCounts();
      this.load();
    }
  },
  methods: {
    money,
    statusChange,
    billMonth,
    clearFilter() {
      this.filter = '';
      this.load();
    },
    loadCounts() {
      this.$axios
        .get(`${this.api}/counts`)
        .then((res) => {
          this.counts = res.data.data || {};
        })
        .catch(() => {});
    },
    load() {
      if (!this.step) return;
      this.loading = true;
      this.$axios
        .get(`${this.api}/pending`, { params: { step: this.step, per_page: 200, filter: this.filter || undefined } })
        .then((res) => {
          this.requests = (res.data.data.data || []).map((r) => ({ ...r, selectable: !r.acted_by_me }));
          this.selected = [];
        })
        .catch((error) => this.toast('error', this.message(error)))
        .finally(() => {
          this.loading = false;
        });
    },
    openReject() {
      this.rejectReason = '';
      this.rejectError = '';
      this.rejectDialog = true;
    },
    approve() {
      this.working = true;
      this.$axios
        .post(`${this.api}/approve`, { step: this.step, ids: this.selected.map((r) => r.id) })
        .then((res) => {
          const d = res.data.data;
          this.failed = d.failed || [];
          const done = this.step === 3 ? `${this.finalWord}ແລ້ວ ${d.done.length} ລາຍ` : `ອະນຸມັດແລ້ວ ${d.approved.length} ລາຍ`;
          this.toast(this.failed.length ? 'warning' : 'success', this.failed.length ? `${done}, ບໍ່ສຳເລັດ ${this.failed.length}` : done);
          this.approveDialog = false;
          this.loadCounts();
          this.load();
        })
        .catch((error) => this.toast('error', this.message(error)))
        .finally(() => {
          this.working = false;
        });
    },
    reject() {
      if (!this.rejectReason || !this.rejectReason.trim()) {
        this.rejectError = 'ກະລຸນາໃສ່ເຫດຜົນ';
        return;
      }
      this.working = true;
      this.$axios
        .post(`${this.api}/reject`, { step: this.step, ids: this.selected.map((r) => r.id), reason: this.rejectReason.trim() })
        .then((res) => {
          const d = res.data.data;
          this.failed = d.failed || [];
          this.toast(this.failed.length ? 'warning' : 'success', `ປະຕິເສດແລ້ວ ${d.rejected.length} ລາຍ`);
          this.rejectDialog = false;
          this.loadCounts();
          this.load();
        })
        .catch((error) => this.toast('error', this.message(error)))
        .finally(() => {
          this.working = false;
        });
    },
    message(error) {
      return (error.response && error.response.data && error.response.data.message) || 'Something went wrong';
    },
    toast(color, msg) {
      this.$store.commit('Toast_State', { value: true, color, msg });
    },
  },
};
</script>
